/**
 * JWT token utilities.
 *
 * Decodes JWT payload without verifying signature (signature verification
 * is handled by the backend). Used client-side to extract scopes, tenant_id,
 * and expiration from the access token.
 */

export interface JwtPayload {
  sub: string
  tenant_id: string
  scopes: string[]
  permissions: string[]
  role: string
  type: 'access' | 'refresh'
  jti: string
  iat: number
  exp: number
}

/**
 * Decode a JWT token and return its payload.
 * Does NOT verify the signature — that's the backend's job.
 * Returns null if the token is malformed.
 */
export function decodeJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null

    const payload = parts[1]
    const decoded = JSON.parse(atob(payload)) as JwtPayload

    // Validate expected fields exist
    if (!decoded.sub || !decoded.tenant_id) return null

    return decoded
  } catch {
    return null
  }
}

/**
 * Check if a JWT token is expired.
 * Returns true if the token has no exp claim or is expired.
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJwt(token)
  if (!payload?.exp) return true

  const now = Math.floor(Date.now() / 1000)
  return payload.exp < now
}
